import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHead from '../Components/SEOHead';
import PageBreadcrumbs from '../Components/PageBreadcrumbs';
import { FaCalendarAlt, FaUser, FaClock, FaArrowLeft, FaTag, FaFacebook, FaTwitter, FaLinkedin } from 'react-icons/fa';
import blogService from '../services/blogService';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPost = async () => {
      try {
        setIsLoading(true);
        const data = await blogService.getAllPosts();
        // Filter out test and elderly posts
        const validPosts = data.filter(post => {
          const titleLower = (post.title || '').toLowerCase();
          const slugLower = (post.slug || '').toLowerCase();
          return !titleLower.includes('test') && 
                 !slugLower.includes('test') &&
                 !titleLower.includes('elderly');
        });
        const foundPost = validPosts.find(p => p.slug === slug || p.id === parseInt(slug));
        setPost(foundPost || null);
        
        if (foundPost) {
          const related = validPosts.filter(p => 
            p.category === foundPost.category && 
            p.id !== foundPost.id
          ).slice(0, 3);
          setRelatedPosts(related);
        }
      } catch (error) {
        console.error('Error loading blog post:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadPost();
  }, [slug]);

  const sampleBlogPosts = [
    {
      id: 1,
      title: "Understanding Plantar Fasciitis: Causes and Treatment",
      excerpt: "Plantar fasciitis is one of the most common causes of heel pain. Learn about the causes, symptoms, and effective treatment options for this condition.",
      content: `
        <h2>What is Plantar Fasciitis?</h2>
        <p>Plantar fasciitis is a common foot condition that causes pain in the heel and bottom of the foot. It occurs when the plantar fascia, a thick band of tissue that connects the heel bone to the toes, becomes inflamed or irritated.</p>
        
        <h2>Causes of Plantar Fasciitis</h2>
        <p>Several factors can contribute to the development of plantar fasciitis:</p>
        <ul>
          <li><strong>Overuse:</strong> Repetitive stress on the plantar fascia from activities like running, walking, or standing for long periods</li>
          <li><strong>Foot Structure:</strong> High arches, flat feet, or abnormal walking patterns</li>
          <li><strong>Age:</strong> Most common in people between 40 and 60 years old</li>
          <li><strong>Obesity:</strong> Extra weight puts additional stress on the plantar fascia</li>
          <li><strong>Improper Footwear:</strong> Shoes with poor arch support or cushioning</li>
        </ul>

        <h2>Symptoms</h2>
        <p>The most common symptoms of plantar fasciitis include:</p>
        <ul>
          <li>Sharp pain in the heel, especially in the morning or after periods of rest</li>
          <li>Pain that decreases with activity but returns after prolonged standing or walking</li>
          <li>Tenderness in the bottom of the heel</li>
          <li>Stiffness in the foot</li>
        </ul>

        <h2>Treatment Options</h2>
        <p>Fortunately, most cases of plantar fasciitis can be treated effectively with conservative measures:</p>

        <h3>1. Rest and Ice</h3>
        <p>Reduce activities that aggravate the pain and apply ice to the affected area for 15-20 minutes several times a day.</p>

        <h3>2. Stretching Exercises</h3>
        <p>Regular stretching of the calf muscles and plantar fascia can help relieve pain and improve flexibility. Some effective stretches include:</p>
        <ul>
          <li>Calf stretches against a wall</li>
          <li>Towel stretches for the plantar fascia</li>
          <li>Rolling a tennis ball under the foot</li>
        </ul>

        <h3>3. Proper Footwear</h3>
        <p>Wear shoes with good arch support and cushioning. Avoid walking barefoot, especially on hard surfaces.</p>

        <h3>4. Orthotics</h3>
        <p>Custom orthotics or over-the-counter arch supports can help distribute pressure more evenly across the foot.</p>

        <h3>5. Physical Therapy</h3>
        <p>A physical therapist can teach you specific exercises and techniques to strengthen the foot and ankle muscles.</p>

        <h2>When to Seek Professional Help</h2>
        <p>If your symptoms persist despite conservative treatment, or if you experience severe pain, it's important to consult with a podiatrist. They can provide:</p>
        <ul>
          <li>Comprehensive evaluation and diagnosis</li>
          <li>Custom treatment plans</li>
          <li>Advanced treatment options if needed</li>
        </ul>

        <h2>Prevention Tips</h2>
        <p>To prevent plantar fasciitis from recurring:</p>
        <ul>
          <li>Maintain a healthy weight</li>
          <li>Wear supportive footwear</li>
          <li>Gradually increase activity levels</li>
          <li>Stretch regularly</li>
          <li>Avoid walking barefoot on hard surfaces</li>
        </ul>

        <p>Remember, early intervention is key to successful treatment of plantar fasciitis. If you're experiencing heel pain, don't hesitate to seek professional advice.</p>
      `,
      author: "Dr. Sarah Johnson",
      date: "2024-01-15",
      readTime: "5 min read",
      category: "Foot Health",
      image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=400&fit=crop",
      tags: ["plantar fasciitis", "heel pain", "foot care"],
      featured: true
    },
    {
      id: 2,
      title: "The Importance of Proper Footwear for Diabetic Patients",
      excerpt: "Choosing the right footwear is crucial for diabetic patients to prevent complications and maintain foot health. Discover what to look for in diabetic-friendly shoes.",
      content: `
        <h2>Why Footwear Matters for Diabetic Patients</h2>
        <p>Diabetes can cause nerve damage and poor blood circulation in the feet, making proper footwear essential for preventing serious complications such as ulcers and infections.</p>
        
        <h2>Key Features of Diabetic-Friendly Shoes</h2>
        <p>When selecting footwear, diabetic patients should look for these important features:</p>
        <ul>
          <li><strong>Seamless Interior:</strong> Reduces the risk of irritation and blisters</li>
          <li><strong>Wide Toe Box:</strong> Provides adequate space for toes to move freely</li>
          <li><strong>Good Arch Support:</strong> Helps distribute weight evenly</li>
          <li><strong>Cushioned Sole:</strong> Absorbs shock and reduces pressure points</li>
          <li><strong>Breathable Material:</strong> Helps keep feet dry and comfortable</li>
        </ul>
      `,
      author: "Dr. Michael Chen",
      date: "2024-01-10",
      readTime: "4 min read",
      category: "Diabetic Care",
      image: "https://images.unsplash.com/photo-1549298916-b41d501d3772?w=800&h=400&fit=crop",
      tags: ["diabetes", "footwear", "prevention"],
      featured: false
    },
    {
      id: 3,
      title: "Home Exercises for Stronger Feet and Ankles",
      excerpt: "Strengthen your feet and ankles with these simple exercises you can do at home. Improve balance, stability, and overall foot health.",
      content: `
        <h2>Benefits of Strong Feet and Ankles</h2>
        <p>Strong feet and ankles are essential for maintaining balance, preventing injuries, and supporting overall mobility. Regular exercise can help improve strength, flexibility, and stability.</p>
        
        <h2>Effective Home Exercises</h2>
        <p>Here are some simple exercises you can do at home to strengthen your feet and ankles:</p>
        
        <h3>1. Toe Curls</h3>
        <p>Sit in a chair and place a towel on the floor. Use your toes to scrunch the towel toward you, then release. Repeat 10-15 times.</p>
        
        <h3>2. Calf Raises</h3>
        <p>Stand with your feet shoulder-width apart. Rise up onto your toes, then slowly lower back down. Repeat 15-20 times.</p>
        
        <h3>3. Ankle Circles</h3>
        <p>Sit with your legs extended. Rotate your ankles in circles, first clockwise, then counterclockwise. Do 10 circles in each direction.</p>
      `,
      author: "Dr. Emma Wilson",
      date: "2024-01-05",
      readTime: "6 min read",
      category: "Exercise",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=400&fit=crop",
      tags: ["exercises", "strength", "balance"],
      featured: false
    }
  ];


  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Blog Post Not Found</h1>
          <Link to="/blog" className="text-primary hover:text-secondary">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // SEO structured data for individual blog post
  const blogPostStructuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image,
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Drosh The Pod",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.droshpod.com/logo.png"
      }
    },
    "datePublished": post.date,
    "dateModified": post.date,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.droshpod.com/blog/${post.id}`
    }
  };

  return (
    <>
      <SEOHead 
        title={`${post.title} | Drosh The Pod Blog`}
        description={post.excerpt}
        keywords={post.tags.join(', ')}
        canonical={`https://www.droshpod.com/blog/${post.id}`}
        structuredData={blogPostStructuredData}
      />
      
      <main className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 pt-6">
          <PageBreadcrumbs />
        </div>

        {/* Back to Blog */}
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/blog"
            className="inline-flex items-center text-primary hover:text-secondary transition-colors"
          >
            <FaArrowLeft className="mr-2" />
            Back to Blog
          </Link>
        </div>

        {/* Article Header */}
        <section className="py-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                {/* Category Badge */}
                <div className="mb-6">
                  <span className="bg-secondary text-white px-4 py-2 rounded-full text-sm font-semibold">
                    {post.category}
                  </span>
                </div>

                {/* Title */}
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6 leading-tight">
                  {post.title}
                </h1>

                {/* Meta Information */}
                <div className="flex flex-wrap items-center text-gray-600 mb-8 space-x-6">
                  <div className="flex items-center">
                    <FaUser className="mr-2" />
                    <span>{post.author}</span>
                  </div>
                  <div className="flex items-center">
                    <FaCalendarAlt className="mr-2" />
                    <span>
                      {new Date(post.date).toLocaleDateString('en-GB', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <FaClock className="mr-2" />
                    <span>{post.readTime}</span>
                  </div>
                </div>

                {/* Featured Image */}
                <div className="mb-8">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-lg"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Main Content */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="lg:col-span-3"
                >
                  <article className="bg-white rounded-2xl shadow-lg p-8">
                    <div 
                      className="prose prose-lg max-w-none"
                      dangerouslySetInnerHTML={{ __html: post.content }}
                    />
                    
                    {/* Tags */}
                    <div className="mt-8 pt-8 border-t border-gray-200">
                      <div className="flex items-center flex-wrap gap-2">
                        <FaTag className="text-gray-400" />
                        {post.tags.map(tag => (
                          <span
                            key={tag}
                            className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </article>
                </motion.div>

                {/* Sidebar */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="lg:col-span-1"
                >
                  <div className="space-y-6">
                    {/* Share Section */}
                    <div className="bg-white rounded-xl shadow-lg p-6">
                      <h3 className="text-lg font-bold text-gray-800 mb-4">Share This Article</h3>
                      <div className="flex space-x-3">
                        <button className="bg-secondary text-white p-3 rounded-lg hover:bg-primary transition-colors">
                          <FaFacebook />
                        </button>
                        <button className="bg-primary text-white p-3 rounded-lg hover:bg-secondary transition-colors">
                          <FaTwitter />
                        </button>
                        <button className="bg-blue-700 text-white p-3 rounded-lg hover:bg-blue-800 transition-colors">
                          <FaLinkedin />
                        </button>
                      </div>
                    </div>

                    {/* Author Info */}
                    <div className="bg-white rounded-xl shadow-lg p-6">
                      <h3 className="text-lg font-bold text-gray-800 mb-4">About the Author</h3>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold mr-4">
                          {post.author.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{post.author}</p>
                          <p className="text-sm text-gray-600">Podiatrist</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-2xl font-bold text-gray-800 mb-8"
                >
                  Related Articles
                </motion.h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {relatedPosts.map((relatedPost, index) => (
                    <motion.article
                      key={relatedPost.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                    >
                      <div className="relative h-48">
                        <img
                          src={relatedPost.image}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-secondary text-white px-2 py-1 rounded-full text-xs font-semibold">
                            {relatedPost.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center text-xs text-gray-500 mb-3">
                          <FaCalendarAlt className="mr-1" />
                          {new Date(relatedPost.date).toLocaleDateString('en-GB', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                          <FaClock className="ml-3 mr-1" />
                          {relatedPost.readTime}
                        </div>
                        <h3 className="text-lg font-bold text-gray-800 mb-3 line-clamp-2">
                          {relatedPost.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {relatedPost.excerpt}
                        </p>
                        <Link
                          to={`/blog/${relatedPost.id}`}
                          className="inline-flex items-center text-primary text-sm font-semibold hover:text-secondary transition-colors"
                        >
                          Read More
                          <FaArrowLeft className="ml-1 rotate-180" />
                        </Link>
                      </div>
                    </motion.article>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-primary to-secondary">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-3xl font-bold text-white mb-4"
              >
                Need Professional Foot Care?
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-white/90 mb-8"
              >
                If you're experiencing foot pain or have concerns about your foot health, 
                our experienced podiatrists are here to help. Book a consultation today.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Link
                  to="/ConsultationForm"
                  className="bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors inline-block"
                >
                  Book Consultation
                </Link>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default BlogPost;